import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { certApi } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { IconCompliance, IconGovernment, IconCyber, IconDataMonitor } from "../brand/icons";
import { GraduationCap, Award, Sparkles } from "lucide-react";

const TRACK_ICONS = {
  municipal_transparency: IconGovernment,
  infrastructure_auditor: IconCompliance,
  cyber_infrastructure: IconCyber,
  platform_operator: IconDataMonitor,
};

const FUNDING_OPTIONS = [
  ["self_funded",       "Self-funded"],
  ["agency_sponsored",  "Agency-sponsored"],
  ["grant",             "Grant-supported"],
  ["pilot_partner",     "Pilot partner"],
  ["student",           "Student / early-career"],
  ["no_cost_request",   "Requesting no-cost pathway"],
];

const Certification = () => {
  const [tracks, setTracks] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "", role: "",
    location: "", track: "municipal_transparency", reason: "",
    funding_status: "no_cost_request",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    certApi.tracks().then(r => { setTracks(r.tracks || []); setPathways(r.no_cost_pathways || []); });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await certApi.apply(form);
      toast.success(r.message || "Application received");
      setForm({ ...form, name: "", email: "", phone: "", organization: "",
                role: "", location: "", reason: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    }
    setBusy(false);
  };

  return (
    <Layout>
      <SEOHead title="AuditIQ Certification Program | No-Cost Pathways Available" path="/certification"
        description="AuditIQ Certification Program: Certified Municipal Transparency Officer, Infrastructure Transparency Auditor, Cyber Infrastructure Monitor, AuditIQ Platform Operator — with no-cost pathways for eligible agencies, students, and pilot partners." />
      <PageHeader eyebrow="Certification"
        title="AuditIQ Certification Program"
        subtitle="Train municipal staff, infrastructure professionals, data-center operators, and transparency officers to operate the AuditIQ platform. No-cost pathways available for eligible participants."
      />

      {/* Tracks */}
      <section className="max-w-[1400px] mx-auto px-6 py-14">
        <SectionHead num="01" label="Certification Tracks" title="Four tracks. One platform."/>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {tracks.map(t => {
            const Icon = TRACK_ICONS[t.key] || Award;
            return (
              <div key={t.key} className="glass p-6" data-testid={`track-${t.key}`}>
                <Icon size={28} />
                <div className="font-display text-lg mt-4">{t.title}</div>
                <div className="text-xs text-steel mt-2">{t.hours} training hours</div>
                <div className="text-sm text-cream-dim mt-3 leading-relaxed">{t.audience}</div>
                <div className="gold-rule mt-4" />
                <button
                  className="mt-3 text-xs uppercase tracking-[0.22em] text-gold hover:text-electric transition-colors"
                  onClick={() => { setForm({ ...form, track: t.key }); window.scrollTo({ top: document.getElementById("apply").offsetTop - 40, behavior: "smooth" }); }}
                  data-testid={`apply-${t.key}`}
                >
                  Apply →
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* No-cost pathway */}
      <section className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="glass-accent p-8">
          <div className="flex items-start gap-5">
            <Sparkles size={40} className="text-electric shrink-0" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Learn How to Get Certified at No Cost</div>
              <h2 className="font-display text-3xl md:text-4xl mt-2">No-Cost Certification Pathway</h2>
              <p className="text-cream-dim mt-3 max-w-4xl leading-relaxed">
                Eligible agencies, staff, students, pilot partners, and sponsored participants may qualify
                for <strong className="text-gold">no-cost certification</strong> through one of the following pathways:
              </p>
              <ul className="grid md:grid-cols-2 gap-2 mt-4 max-w-3xl">
                {pathways.map(p => (
                  <li key={p} className="flex items-center gap-2 text-sm text-cream-dim">
                    <GraduationCap size={14} className="text-electric" /> {p}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="#apply"><Button className="btn-gold h-11 px-5" data-testid="cta-no-cost">Apply for No-Cost Certification</Button></a>
                <Link to="/contact?form=agency_sponsorship"><Button className="btn-ghost-gold h-11 px-5" data-testid="cta-agency">Request Agency Sponsorship</Button></Link>
                <Link to="/pricing"><Button className="btn-ghost-gold h-11 px-5" data-testid="cta-pilot">Join Pilot Program</Button></Link>
                <Link to="/contact?form=certification"><Button className="btn-ghost-gold h-11 px-5" data-testid="cta-info">Request Certification Information</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="max-w-[1100px] mx-auto px-6 py-16">
        <SectionHead num="02" label="Apply" title="Submit your certification interest"/>
        <form onSubmit={submit} className="glass p-8 mt-8 grid md:grid-cols-2 gap-5" data-testid="cert-form">
          <Field label="Full name" required>
            <Input required value={form.name} onChange={set("name")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-name" />
          </Field>
          <Field label="Work email" required>
            <Input required type="email" value={form.email} onChange={set("email")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-email" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set("phone")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-phone" />
          </Field>
          <Field label="Organization">
            <Input value={form.organization} onChange={set("organization")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-org" />
          </Field>
          <Field label="Role / Title">
            <Input value={form.role} onChange={set("role")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-role" />
          </Field>
          <Field label="City / County / Agency">
            <Input value={form.location} onChange={set("location")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-location" />
          </Field>
          <Field label="Certification track" required>
            <Select value={form.track} onValueChange={set("track")}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="cert-track"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {tracks.map(t => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Funding status" required>
            <Select value={form.funding_status} onValueChange={set("funding_status")}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="cert-funding"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {FUNDING_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Reason for request">
              <Textarea rows={4} value={form.reason} onChange={set("reason")} className="bg-navy-deep border-gold-dim/40" data-testid="cert-reason" />
            </Field>
          </div>
          <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-steel">
              Applications are reviewed within 2 business days. No-cost requests require organizational verification.
            </div>
            <Button disabled={busy} className="btn-gold h-12 px-8" data-testid="cert-submit">
              {busy ? "Submitting…" : "Submit Application"}
            </Button>
          </div>
        </form>
      </section>
    </Layout>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.22em] text-steel">
      {label}{required && <span className="text-gold ml-1">*</span>}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);

const SectionHead = ({ num, label, title }) => (
  <div>
    <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-electric">
      <span className="inline-block w-6 h-px bg-electric" /> {num} · {label}
    </div>
    <h2 className="font-display text-3xl md:text-4xl mt-3">{title}</h2>
    <div className="data-line mt-4" />
  </div>
);

export default Certification;
